import { ref, readonly } from "vue";
import { useI18n } from "vue-i18n";
import { useGetWTFIR, useLazyProductsLoad, useLazyFindPairs, WTFIRInputType, WTFIRTypeData } from "@src/api";

type StepInfo = {
  value?: string;
  type?: WTFIRTypeData;
  gtin?: string;
};

type ItemInfo = {
  id: string;
  isNonMarked: boolean;
};

type PairInfo = {
  epc?: string;
  cis?: string;
};

type CheckPairsData = {
  isFirstScannedRfid: boolean;
  barcode: StepInfo | null;
  rfid: StepInfo | null;
  product: ItemInfo | null;
  pair: PairInfo;
};

const initData: CheckPairsData = {
  isFirstScannedRfid: false,
  barcode: null,
  rfid: null,
  product: null,
  pair: null,
};

const initSteps = {
  first: null,
  second: null,
  quarantine: false,
};

const steps = ref<{
  first: { status: "start" | "success" | "not-found" } | null;
  second: { status: "start" | "success" | "not-found" } | null;
  quarantine: boolean;
}>({ ...initSteps });
const data = ref<CheckPairsData>({ ...initData });

export const useCheckPairs = () => {
  const { t } = useI18n();
  const { execute: getWTFIR, isLoading: isWTFIRLoading } = useGetWTFIR();
  const { execute: getProduct, isLoading: isProductLoading } = useLazyProductsLoad();
  const { execute: getFindPairs } = useLazyFindPairs();

  /**
   * Загружает данные из WTFIR, product и pair
   * для чистой EPC и неизвестного типа данных загрузка не производится
   * Product загружается только если он не был загружен ранее
   * Pair загружается только если он не был загружен ранее и тип данных не qr
   * @param inputScanType - тип ввода данных
   */
  const load = async (inputScanType: WTFIRInputType) => {
    const resultWTFIR = await getWTFIR({ data: data.value[inputScanType].value, input_type: inputScanType });

    if (resultWTFIR.isError.value) return;

    data.value[inputScanType].gtin = resultWTFIR.data.value.gtin;
    data.value[inputScanType].type = resultWTFIR.data.value.type_data;
    const productId = resultWTFIR.data.value.items?.at(0);

    if ([WTFIRTypeData.unknown, WTFIRTypeData.epcClear].includes(resultWTFIR.data.value.type_data)) return;

    if (productId && !data.value.product) {
      const resultProduct = await getProduct({ item_id: [productId] });

      if (resultProduct.isError.value) return;

      data.value.product = {
        id: productId,
        isNonMarked: !resultProduct.data.value.items.at(0)?.is_marked,
      };
    }

    if (!data.value.pair && resultWTFIR.data.value.type_data !== WTFIRTypeData.qr) {
      const pairResult = await getFindPairs({ [inputScanType === WTFIRInputType.barcode ? "cises" : "epces"]: [data.value[inputScanType].value] });

      if (pairResult.isError.value) return;

      data.value.pair = {
        epc: pairResult.data.value.pairs.at(0)?.epc,
        cis: pairResult.data.value.pairs.at(0)?.cis,
      };
    }
  };

  /**
   * Проверяет валидность данных
   * @returns true - если данные валидны, false - если данные не валидны
   */
  const validation = () => {
    // Если был отсканирвоан КИЗ/QR, то обязательно должен быть gtin товара и сам товар (item|product)
    if (data.value.barcode?.value && [WTFIRTypeData.cis, WTFIRTypeData.qr].includes(data.value.barcode.type)) {
      if (!data.value.barcode.gtin || !data.value.product) {
        return false;
      }
    }

    // Если был отсканирован EPC, то обязательно должен быть gtin товара и сам товар (item|product)
    if (data.value.rfid?.value && data.value.rfid.type === WTFIRTypeData.epc) {
      if (!data.value.rfid.gtin || !data.value.product) {
        return false;
      }
    }

    // Если был отсканирован EPC и КИЗ, то обязательно должен быть пара (pair), только если тип данных не QR
    if (data.value.rfid?.value && data.value.barcode?.value) {
      if (!data.value.pair && data.value.barcode.type !== WTFIRTypeData.qr) {
        return false;
      }
    }

    return true;
  }

  const checkBarcodeType = () => {
    return [WTFIRTypeData.cis, WTFIRTypeData.qr].includes(data.value.barcode?.type);
  }

  const checkRfidType = () => {
    return [WTFIRTypeData.epc, WTFIRTypeData.epcClear].includes(data.value.rfid?.type);
  }

  /**
   * Проверяет данные
   * @returns next - следующий шаг
   * @returns epc-write - запись EPC
   * @returns success - пара корректна
   * @returns set-pair - создать валидную пару
   * @returns incorrect - пара не корректна
   */
  const check = (): { stage: string; action?: string; message?: string; type?: string } => {
    // если тип данных не корректный
    if ((data.value.barcode?.type && !checkBarcodeType()) || (data.value.rfid?.type && !checkRfidType())) {
      return { stage: 'initial', message: t("Некорректный") + " " + (data.value.isFirstScannedRfid ? t("КИЗ") : t("EPC")), type: "error" };
    }

    // нехватает данных EPC/КИЗ|QR
    if (!data.value.barcode || !data.value.rfid) {
      return {
        stage: 'initial',
        action: 'next',
        ...(data.value.rfid?.type === WTFIRTypeData.epcClear ? { message: t("Чистый EPC"), type: "warning" } : {})
      };
    }

    // КИЗ валидный, а EPC метка чистая
    // Если чистая EPC была первой, то сообщение выводилось на этапе выше 'нехватает данных EPC/КИЗ|QR'
    if (data.value.rfid.type === WTFIRTypeData.epcClear) {
      return {
        stage: 'initial',
        action: 'epc-write',
        ...(!data.value.isFirstScannedRfid ? { message: t("Чистый EPC"), type: "warning" } : {})
      };
    }

    // QR - в QRкоде закодирован EPC и они должны быть равны
    if (data.value.barcode.type === WTFIRTypeData.qr && data.value.product?.isNonMarked) {
      return data.value.barcode.value === data.value.rfid.value
        ? { stage: 'success' }
        : { stage: 'error', message: t("Пара EPC и КИЗ не корректна"), type: "error" };
    }

    // КИЗ и EPC от одной|разных пар[ы]
    if (data.value.barcode.gtin === data.value.rfid.gtin) {
      if (data.value.barcode.value === data.value.pair.cis && data.value.rfid.value === data.value.pair.epc) {
        return { stage: 'success' };
      }

      return { stage: 'error', action: 'set-pair' };
    }
  }

  /**
   * Сбрасывает данные к первоначальному состоянию
   */
  const reset = () => {
    data.value = { ...initData };
  }

  /**
   * Устанавливает данные
   * @param nextData - данные для установки
   */
  const set = (nextData: Partial<CheckPairsData>) => {
    data.value = { ...data.value, ...nextData };
  };

  /**
   * Устанавливает шаг
   * @param step - шаг
   * @param status - статус
   */
  const setStep = (step: "first" | "second", status: "start" | "success" | "not-found") => {
    if (step === "first" && steps.value.second) {
      steps.value.second = null;
    }

    if (step === "second" && steps.value.first) {
      steps.value.first = null;
    }

    steps.value[step] = { status };
  };

  /**
   * Включение/выключение карантина
   * @param quarantine - true - включить карантин, false - выключить карантин
   */
  const setQuarantine = (quarantine: boolean) => {
    steps.value.quarantine = quarantine;
  };

  /**
   * Сбрасывает шаги к первоначальному состоянию
   */
  const resetStep = () => {
    steps.value = { ...initSteps };
  };

  return {
    steps: {
      value: readonly(steps),
      set: setStep,
      reset: resetStep,
      setQuarantine,
    },
    data: {
      value: readonly(data),
      set,
      reset,
      load,
      check,
      validation,
      isLoading: isWTFIRLoading || isProductLoading,
    },
  };
};